import { useReducer, useMemo } from 'react';

const getInitialParams = (): Params => {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const storeIds = userData?.userDetails?.storeIds;
  const storeId = Array.isArray(storeIds) && storeIds.length > 0 ? storeIds[0] : '';
  const merchantId = userData?.userId || '';

  return {
    sortBy: 'createdAt',
    sortOrder: 'asc',
    page: 1,
    limit: 10,
    search: undefined,
    storeId,
    statusFilter: 'all',
    addedByFilter: 'all',
    storeTypeFilter: undefined,
    merchantId,
  };
};

type Params = {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
  search?: string;
  storeId: string;
  statusFilter: 'blocked' | 'unblocked' | 'all';
  addedByFilter: 'Admin' | 'Merchant' | 'all';
  storeTypeFilter?: string;
  merchantId: string;
};

type Action =
  | { type: 'SET_SEARCH'; payload: string | undefined }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_LIMIT'; payload: number }
  | { type: 'SET_SORT'; payload: { sortBy: string; sortOrder: 'asc' | 'desc' } }
  | { type: 'SET_STATUS_FILTER'; payload: 'blocked' | 'unblocked' | 'all' }
  | { type: 'SET_ADDED_BY_FILTER'; payload: 'Admin' | 'Merchant' | 'all' }
  | { type: 'SET_STORE_TYPE_FILTER'; payload: string | undefined }
  | { type: 'RESET' };

function reducer(state: Params, action: Action): Params {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, search: action.payload, page: 1 };
    case 'SET_PAGE':
      return state.page === action.payload ? state : { ...state, page: action.payload };
    case 'SET_LIMIT':
      return { ...state, limit: action.payload, page: 1 };
    case 'SET_SORT':
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder,
        page: 1,
      };
    case 'SET_STATUS_FILTER':
      return { ...state, statusFilter: action.payload, page: 1 };
    case 'SET_ADDED_BY_FILTER':
      return { ...state, addedByFilter: action.payload, page: 1 };
    case 'SET_STORE_TYPE_FILTER':
      return { ...state, storeTypeFilter: action.payload, page: 1 };
    case 'RESET':
      return getInitialParams();
    default:
      return state;
  }
}

export function useMealParams() {
  const [params, dispatch] = useReducer(reducer, getInitialParams());

  // Memoize params so the reference only changes when values actually change
  const stableParams = useMemo(() => params, [
    params.sortBy,
    params.sortOrder,
    params.page,
    params.limit,
    params.storeId,
    params.statusFilter,
    params.addedByFilter,
    params.storeTypeFilter,
    params.search,
    params.merchantId,
  ]);

  return { params: stableParams, dispatch };
}
